import type { Key, ReactNode } from 'react';
import { Select as HeroSelect } from '@heroui/react/select';
import { ListBox } from '@heroui/react/list-box';
import { ListBoxItem } from '@heroui/react/list-box-item';
import { Label } from '@heroui/react/label';
import { Description } from '@heroui/react/description';
import { FieldError } from '@heroui/react/field-error';
import { cn } from '@/utils/cn';

/**
 * Rich select — HeroUI's Select over a ListBox.
 *
 * Use this when options need more than a text label (icons, secondary
 * detail, prices). For a plain list bound to react-hook-form `register()`,
 * the native `Select` in `ds/Input` is still the simpler choice.
 */
export interface SelectOption {
  value: string;
  /** Text used for the trigger and for type-ahead matching. */
  label: string;
  /** Optional richer row content. Falls back to `label`. */
  content?: ReactNode;
  disabled?: boolean;
}

export interface SelectMenuProps {
  value?: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: ReactNode;
  placeholder?: string;
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  /** Shown inside the popover when `options` is empty. */
  emptyMessage?: ReactNode;
  className?: string;
  /**
   * `field` (default) is a full-width form control. `filter` is a compact,
   * content-sized chip for a filter bar: 32px tall, optional leading `icon`,
   * and tinted while it holds anything but the empty "all" value — so a
   * glance at the bar says which filters are narrowing the list.
   */
  variant?: 'field' | 'filter';
  /**
   * `filter` variant only: whether the chip shows as narrowing the list.
   * Defaults to "has a non-empty value", the empty value being "all".
   */
  active?: boolean;
  /** Leading icon, `filter` variant only. */
  icon?: ReactNode;
  /** Required when there is no visible `label` (the `filter` variant). */
  'aria-label'?: string;
}

export function SelectMenu({
  value,
  onChange,
  options,
  label,
  placeholder = 'Select…',
  hint,
  error,
  disabled,
  required,
  emptyMessage,
  className,
  variant = 'field',
  icon,
  active,
  'aria-label': ariaLabel,
}: SelectMenuProps) {
  const isFilter = variant === 'filter';
  const isActive = isFilter && (active ?? !!value);

  return (
    <HeroSelect
      selectedKey={value ?? null}
      onSelectionChange={(k: Key | null) => onChange(k ? String(k) : '')}
      isDisabled={disabled}
      isRequired={required}
      isInvalid={!!error}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={cn(
        'flex flex-col gap-1.5',
        isFilter ? 'w-auto max-w-full' : 'w-full',
        className,
      )}
    >
      {label && <Label>{label}</Label>}

      <HeroSelect.Trigger
        className={
          isFilter
            ? cn(
                'ds-hit h-8 min-h-8 items-center gap-1.5 rounded-full py-0 ps-3 text-small shadow-none',
                isActive && 'border-primary-200 bg-primary-50 text-brand-text',
              )
            : undefined
        }
      >
        {isFilter && icon && (
          <span
            aria-hidden
            className={cn(
              'flex shrink-0 [&>svg]:size-3.5',
              isActive ? 'text-brand-text' : 'text-muted',
            )}
          >
            {icon}
          </span>
        )}
        <HeroSelect.Value
          className={isFilter ? 'truncate text-small sm:text-small' : undefined}
        />
        <HeroSelect.Indicator />
      </HeroSelect.Trigger>

      {hint && !error && <Description>{hint}</Description>}
      {error && <FieldError>{error}</FieldError>}

      <HeroSelect.Popover>
        <ListBox
          renderEmptyState={
            emptyMessage
              ? () => (
                  <div className="px-3 py-2 text-body text-muted">
                    {emptyMessage}
                  </div>
                )
              : undefined
          }
        >
          {options.map((opt) => (
            <ListBoxItem
              key={opt.value}
              id={opt.value}
              textValue={opt.label}
              isDisabled={opt.disabled}
            >
              {opt.content ?? opt.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </HeroSelect.Popover>
    </HeroSelect>
  );
}
